import { FC, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { slide as Menu } from "react-burger-menu";
import * as React from "react";
import { Button, Accordion, ListGroup } from "react-bootstrap";
import { toast } from "react-toastify";

import { Intersection, StopSign } from "../../map-interface.d";
import { Checkbox } from "../common/Checkbox";
import {
  getMapStructs,
  removeLaneAndUpdateFeatures,
  resetSpeedLimits,
} from "../../store/slices/mapStructs";

import {
  BmItemTitleStyled,
  CardBodyStyled,
  CardContainerStyled,
  CardHeaderStyled,
  CardStyled,
  EditPlaneButtonStyled,
  EditPlaneToolboxStyled,
  MenuBtnStyled,
  SwitchStyled,
  CancelSelectedLanesButtonStyled,
  ButtonsContainerStyled,
  EditButtonStyled,
  RemoveButtonStyled,
} from "./styles";
import "./side-menu.css";
import { ResetSpeedLimits } from "./components/ResetSpeedLimits";
import { ExpandedLane } from "./components/ExpandedLane";
import { IntersectionsCard } from "./components/IntersectionsCard";
import { CONFIRM_ACTION_MODAL, toggleModal, STATION_MODAL } from "../../store/slices/modals";
import { getFeatures } from "../../store/slices/currentMap";
import { Input } from "../common/Input";
import { getProjects } from "../../store/slices/projects";
import { getCurrentMapId } from "../../store/slices/currentMap";
import { getStationsBySemanticMap } from "../../services/stationApi";
import { PropLineStyled, PropNameStyled } from "./components/styles";

type SideMenuProps = {
  renderEditPlane: boolean;
  onLaneFocus: (laneIds: Array<number>) => void;
  onStopSignFocus: (stopSignId: number) => void;
  setEditPlaneOpen: (open: boolean) => void;
  adjustEditPlaneElevation: (diff: number) => void;
  adjustEditPlaneRadius: (diff: number) => void;
  selectedLaneIds: Array<number>;
  selectedStationIds: string[];
  onStationFocus: (ids: string[]) => void;
  onStationsDataChange: (stations: any[]) => void;
};

export const SideMenu: FC<SideMenuProps> = ({
  renderEditPlane,
  onLaneFocus,
  onStopSignFocus,
  setEditPlaneOpen,
  adjustEditPlaneElevation,
  adjustEditPlaneRadius,
  selectedLaneIds,
  selectedStationIds,
  onStationFocus,
  onStationsDataChange,
}) => {
  const dispatch = useDispatch();
  const [openedTabs, setOpenedTabs] = useState<Array<number>>([]);
  const [searchLaneValue, setSearchLaneValue] = useState<string>("");
  const mapStructs = useSelector(getMapStructs);
  const mapFeatures = useSelector(getFeatures);

  const { lanes } = mapStructs;

  const projects = useSelector(getProjects);
  const currentMapId = useSelector(getCurrentMapId);
  const currentProject = projects.find((p) => p._id === currentMapId);
  const [stations, setStations] = useState<any[]>([]);
  const [isLoadingStations, setIsLoadingStations] = useState(false);
  const [stationsError, setStationsError] = useState<string | null>(null);

  React.useEffect(() => {
    const fetchStations = async () => {
      if (currentProject?.semanticMapId) {
        setIsLoadingStations(true);
        setStationsError(null);
        try {
          const data = await getStationsBySemanticMap(
            currentProject.semanticMapId
          );
          setStations(data);
          onStationsDataChange(data);
        } catch (e) {
          console.error("Failed to fetch stations:", e);
          const errorMessage =
            e instanceof Error ? e.message : "Unknown error occurred";
          setStationsError(errorMessage);
          setStations([]);
          onStationsDataChange([]);
          toast.error(`Failed to load stations: ${errorMessage}`);
        } finally {
          setIsLoadingStations(false);
        }
      } else {
        setStations([]);
        onStationsDataChange([]);
        setStationsError(null);
      }
    };

    // Function to handle stations update event
    const handleStationsUpdated = (event: CustomEvent) => {
      if (event.detail?.semanticMapId === currentProject?.semanticMapId) {
        fetchStations();
      }
    };

    fetchStations();

    // Add event listener for stations updates
    window.addEventListener(
      "stationsUpdated",
      handleStationsUpdated as EventListener
    );

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener(
        "stationsUpdated",
        handleStationsUpdated as EventListener
      );
    };
  }, [currentProject?.semanticMapId, onStationsDataChange]);

  if (mapStructs === undefined) {
    return null;
  }

  const onSearchAction = useCallback(
    (val: any) => setSearchLaneValue(val?.target?.value),
    []
  );

  const removeLaneAction = (laneId: number) => {
    dispatch(
      toggleModal({
        type: CONFIRM_ACTION_MODAL,
        data: {
          text: "Are you sure you want to delete the entire lane?",
          confirmAction: () => {
            try {
              dispatch(removeLaneAndUpdateFeatures({ laneId, mapFeatures }));
              toast.success(`Lane ${laneId} successfully removed`);
            } catch (error: any) {
              const errorMessage = error?.message || "Unknown error occurred";
              toast.error(`Error removing lane: ${errorMessage}`);
            }
          },
        },
      })
    );
  };

  const selectLane = (event: any, laneId: number) => {
    event.preventDefault();
    let newSelectedLanes = [];

    if (selectedLaneIds.includes(laneId)) {
      newSelectedLanes = [...selectedLaneIds.filter((lane) => laneId !== lane)];
    } else {
      newSelectedLanes = [...selectedLaneIds, laneId];
    }
    onLaneFocus(newSelectedLanes);
  };

  const renderEditPlaneToolBox = () => {
    const elev_step = 0.5;
    const radius_step = 0.00005;
    return (
      <EditPlaneToolboxStyled>
        <BmItemTitleStyled>
          Edit Plane
          <SwitchStyled
            name="displayEditPlane"
            value={renderEditPlane ? 1 : 0}
            onChange={() => {
              setEditPlaneOpen(!renderEditPlane);
            }}
          ></SwitchStyled>
        </BmItemTitleStyled>
        <EditPlaneButtonStyled>
          <Button
            variant="light"
            size="sm"
            onClick={() => {
              adjustEditPlaneElevation(elev_step);
            }}
          >
            Up
          </Button>
          <Button
            variant="light"
            size="sm"
            onClick={() => {
              adjustEditPlaneElevation(-elev_step);
            }}
          >
            Down
          </Button>
        </EditPlaneButtonStyled>
        <EditPlaneButtonStyled>
          <Button
            variant="light"
            size="sm"
            onClick={() => {
              adjustEditPlaneRadius(radius_step);
            }}
          >
            +
          </Button>
          <Button
            variant="light"
            size="sm"
            onClick={() => {
              adjustEditPlaneRadius(-radius_step);
            }}
          >
            -
          </Button>
        </EditPlaneButtonStyled>
      </EditPlaneToolboxStyled>
    );
  };

  const setActiveElement = (elementId: number) => {
    if (openedTabs.includes(elementId)) {
      setOpenedTabs([...openedTabs].filter((tabId) => tabId !== elementId));
    } else {
      setOpenedTabs([...openedTabs, elementId]);
    }
  };

  const renderIntersectionList = () => {
    const { intersections } = mapStructs;

    if (mapStructs === undefined || intersections === undefined) {
      return null;
    }

    const listItems: JSX.Element[] = [];

    Object.values(intersections).forEach((intersection: Intersection) => {
      const { intersection_id } = intersection;
      listItems.push(
        <IntersectionsCard
          key={intersection_id}
          isOpened={openedTabs.includes(intersection_id)}
          intersection={intersection}
          setActiveElement={setActiveElement}
          selectedLaneIds={selectedLaneIds}
          onLaneFocus={onLaneFocus}
        />
      );
    });

    return (
      <div>
        <BmItemTitleStyled>Intersections</BmItemTitleStyled>
        <Accordion defaultActiveKey="0">{listItems}</Accordion>
      </div>
    );
  };

  const renderStopSignProps = (stop_sign_props: StopSign) => {
    const propsList: JSX.Element[] = [];
    (Object.keys(stop_sign_props) as Array<keyof StopSign>).forEach((prop) => {
      const currentItem = `${prop}: ${stop_sign_props[prop]}`;
      propsList.push(<ListGroup.Item key={prop}>{currentItem}</ListGroup.Item>);
    });

    return <ListGroup as="ul">{propsList}</ListGroup>;
  };

  const renderStopSignList = () => {
    const { stopSigns } = mapStructs;

    if (mapStructs === undefined || stopSigns === undefined) {
      return null;
    }

    const listItems: JSX.Element[] = [];

    Object.values(stopSigns).forEach((stop_sign_props: any) => {
      const { stop_sign_id } = stop_sign_props;
      listItems.push(
        <CardContainerStyled key={stop_sign_id}>
          <CardHeaderStyled>
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                onStopSignFocus(stop_sign_id);
              }}
            >
              {stop_sign_id}
            </Button>
          </CardHeaderStyled>
          <Accordion.Collapse eventKey={stop_sign_id}>
            <CardBodyStyled>
              {renderStopSignProps(stop_sign_props)}
            </CardBodyStyled>
          </Accordion.Collapse>
        </CardContainerStyled>
      );
    });

    return (
      <div>
        <BmItemTitleStyled>Stop Signs</BmItemTitleStyled>
        <Accordion defaultActiveKey="0">{listItems}</Accordion>
      </div>
    );
  };

  const canselSelectedLanes = () => {
    if (selectedLaneIds.length !== 0) {
      onLaneFocus([]);
    }
  };

  const selectStation = (event: any, stationId: string) => {
    event.preventDefault();
    let newSelectedStations = [];

    if (selectedStationIds.includes(stationId)) {
      newSelectedStations = selectedStationIds.filter((id) => stationId !== id);
    } else {
      newSelectedStations = [...selectedStationIds, stationId];
    }
    onStationFocus(newSelectedStations);
  };

  const handleEditStation = (station: any) => {
    dispatch(
      toggleModal({
        type: STATION_MODAL,
        data: { station },
      })
    );
  };

  const handleDeleteStation = (station: any) => {
    dispatch(
      toggleModal({
        type: STATION_MODAL,
        data: { station, showDeleteConfirmation: true },
      })
    );
  };

  const renderStationProps = (station: any) => {
    return (
      <ListGroup as="ul">
        <PropLineStyled>
          <PropNameStyled>Coordinates:</PropNameStyled>
          <span>
            {station.latitude}, {station.longitude}
          </span>
        </PropLineStyled>
        <ButtonsContainerStyled>
          <EditButtonStyled onClick={() => handleEditStation(station)}>
            Edit
          </EditButtonStyled>
          <RemoveButtonStyled onClick={() => handleDeleteStation(station)}>
            Remove
          </RemoveButtonStyled>
        </ButtonsContainerStyled>
      </ListGroup>
    );
  };

  const renderStationsList = () => {
    if (!currentProject?.semanticMapId) return null;
    return (
      <div>
        <BmItemTitleStyled>Stations</BmItemTitleStyled>
        {isLoadingStations ? (
          <div style={{ color: "#B3D0E6", padding: 8 }}>
            Loading stations...
          </div>
        ) : stationsError ? (
          <div style={{ color: "#ff6b6b", padding: 8 }}>
            Error loading stations: {stationsError}
          </div>
        ) : stations.length === 0 ? (
          <div style={{ color: "#B3D0E6", padding: 8 }}>No stations found.</div>
        ) : (
          <Accordion defaultActiveKey="0">
            {stations.map((station: any) => {
              const stationId = station.id || station.name;
              return (
                <CardContainerStyled key={stationId}>
                  <CardStyled>
                    <MenuBtnStyled
                      onClick={() => {
                        setActiveElement(stationId);
                      }}
                    >
                      <span>{station.name}</span>
                      <Checkbox
                        key={stationId}
                        checked={selectedStationIds.includes(stationId)}
                        onChange={(e: Event) => selectStation(e, stationId)}
                      />
                    </MenuBtnStyled>
                  </CardStyled>
                  {openedTabs.includes(stationId) && (
                    <CardBodyStyled>
                      {renderStationProps(station)}
                    </CardBodyStyled>
                  )}
                </CardContainerStyled>
              );
            })}
          </Accordion>
        )}
      </div>
    );
  };

  return (
    <Menu right={true} isOpen={true}>
      <div>{renderEditPlaneToolBox()}</div>
      <div>
        {lanes && (
          <div>
            <BmItemTitleStyled>Lane Elements</BmItemTitleStyled>
            {Object.values(lanes).length > 0 && (
              <ResetSpeedLimits
                resetAllSpeedLimits={() => dispatch(resetSpeedLimits())}
              />
            )}
            <Input onChange={onSearchAction} labelText="Search Lanes" />
            {selectedLaneIds.length !== 0 && (
              <CancelSelectedLanesButtonStyled onClick={canselSelectedLanes}>
                Cancel Select
              </CancelSelectedLanesButtonStyled>
            )}
            <Accordion defaultActiveKey="0">
              {Object.values(lanes).map((lane: any) => {
                const { lane_id } = lane;
                if (
                  searchLaneValue &&
                  !String(lane_id).includes(searchLaneValue)
                ) {
                  return <></>;
                }

                return (
                  <CardContainerStyled key={lane_id}>
                    <CardStyled>
                      <MenuBtnStyled
                        onClick={() => {
                          setActiveElement(lane_id);
                        }}
                      >
                        <span>{lane_id}</span>
                        <Checkbox
                          key={lane_id}
                          checked={selectedLaneIds.includes(lane_id)}
                          onChange={(e: Event) => selectLane(e, lane_id)}
                        />
                      </MenuBtnStyled>
                    </CardStyled>
                    {openedTabs.includes(lane_id) && (
                      <ExpandedLane lane={lane} removeLane={removeLaneAction} />
                    )}
                  </CardContainerStyled>
                );
              })}
            </Accordion>
          </div>
        )}
      </div>
      <div>{renderIntersectionList()}</div>
      <div>{renderStopSignList()}</div>
      <div>{renderStationsList()}</div>
    </Menu>
  );
};
